import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  User, Mail, Phone, Briefcase, Award, GraduationCap, FileText,
  Upload, Plus, Trash2, CheckCircle2, ArrowRight, ArrowLeft,
  Sparkles, Layers, ShieldCheck, DollarSign, Clock, MapPin, ExternalLink,
  Check
} from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Swal from 'sweetalert2';
import BASE_URL from '../../url';
import '../styles/JoinOurBench.css';

// Brand Logos
import prophecyLogo from '../../Recruitment/Assets/images/prophecy-logo.png';
import prophecyLogo2 from '../../Recruitment/Assets/images/prophecy-logo2.png';

import {
  GENDER_OPTIONS, REMOTE_OPTIONS, STATUS_OPTIONS_FORM, SKILL_TYPE_OPTIONS, SKILL_LEVEL_OPTIONS,
  WORK_AUTHORIZATION_OPTIONS, EMPLOYMENT_TYPE_OPTIONS, SECURITY_CLEARANCE_OPTIONS,
  RATE_TYPE_OPTIONS, MARITAL_STATUS_OPTIONS, INDUSTRY_OPTIONS
} from '../utils/mockData';
import { countriesData, getStatesForCountry } from '../../erprecruitment/config/locationConfig';
import { formatName } from '../utils/nameFormatter';

const emptyEducation = () => ({ Institution: '', Degree: '', FieldOfStudy: '', StartDate: '', EndDate: '', GPA: '' });
const emptyCert = () => ({ CertificationName: '', IssuingOrganization: '', IssueDate: '', ExpiryDate: '', CredentialId: '', CredentialUrl: '' });
const emptyWork = () => ({ Company: '', JobTitle: '', StartDate: '', EndDate: '', IsCurrent: false, Description: '' });
const emptySkill = () => ({ SkillName: '', SkillType: 'Intermediate' });
const emptyDocument = () => ({
  DocumentName: '',
  DocumentType: 'Certificate',
  File: null,
  FileName: '',
  DocumentCategory: 'Other'
});

// Robust option renderer: handles arrays of strings, {code, name}, or {value, label}
const renderOptions = (list, defaultPlaceholder = null) => {
  const elements = [];
  if (defaultPlaceholder) {
    elements.push(
      <option key="__placeholder__" value="">
        {defaultPlaceholder}
      </option>
    );
  }
  (list || []).forEach((item, idx) => {
    let val = '';
    let label = '';
    if (typeof item === 'string') {
      val = item;
      label = item;
    } else if (item && typeof item === 'object') {
      val = item.code !== undefined ? item.code : (item.value !== undefined ? item.value : item.name);
      label = item.name !== undefined ? item.name : (item.label !== undefined ? item.label : item.value);
    }
    if (val !== undefined && label !== undefined) {
      elements.push(
        <option key={`${val}_${idx}`} value={val}>
          {label}
        </option>
      );
    }
  });
  return elements;
};

export default function JoinOurBench() {
  const location = useLocation();
  const navigate = useNavigate();

  // Query params (e.g. ?ref=recruiterName)
  const [referrer, setReferrer] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || params.get('referrer') || params.get('recruiter') || '';
    if (ref) {
      setReferrer(ref);
    }
  }, []);

  // Form State - Identical fields to AddModal
  const [form, setForm] = useState(() => {
    const s = location?.state || {};
    return {
      FirstName: s.FirstName || '',
      LastName: s.LastName || '',
      MiddleName: '',
      JobTitle: s.JobTitle || '',
      YearsOfExperience: '',
      ProfileSummary: '',
      LinkedInUrl: '',
      GitHubUrl: '',
      TwitterUrl: '',
      VideoResumeUrl: '',
      CandidateStatus: 'Available',
      RemoteStatus: 'Remote',
      Email: s.Email || '',
      Phone: s.Phone || '',
      Mobile: s.Mobile || s.Phone || '',
      CurrentLocation: s.LocationCity ? `${s.LocationCity}${s.LocationState ? ', ' + s.LocationState : ''}` : '',
      LocationCountry: s.LocationCountry || 'US',
      LocationState: s.LocationState || '',
      LocationCity: s.LocationCity || '',
      Gender: '',
      WorkAuthorization: s.WorkAuthorization || '',
      EmploymentType: s.EmploymentType || '',
      SecurityClearance: '',
      WillingToRelocate: true,
      IsBench: true, // Always true for bench candidates
      ExpectedRateFrom: '',
      ExpectedRateTo: '',
      ExpectedRateType: 'Hourly',
      CurrentRate: '',
      CurrentRateType: 'Hourly',
      MaritalStatus: '',
      Industry: '',
      CurrentEmployer: '',
      CurrentSalary: '',
      ExpectedSalary: '',
      NoticePeriod: '',
      ResumeFile: s.ResumeFile || null
    };
  });

  useEffect(() => {
    if (location?.state) {
      const s = location.state;
      setForm(prev => ({
        ...prev,
        FirstName: s.FirstName || prev.FirstName,
        LastName: s.LastName || prev.LastName,
        Email: s.Email || prev.Email,
        Phone: s.Phone || prev.Phone,
        Mobile: s.Mobile || s.Phone || prev.Mobile,
        JobTitle: s.JobTitle || prev.JobTitle,
        WorkAuthorization: s.WorkAuthorization || prev.WorkAuthorization,
        LocationCountry: s.LocationCountry || prev.LocationCountry,
        LocationState: s.LocationState || prev.LocationState,
        LocationCity: s.LocationCity || prev.LocationCity,
        CurrentLocation: s.LocationCity ? `${s.LocationCity}${s.LocationState ? ', ' + s.LocationState : ''}` : prev.CurrentLocation,
        EmploymentType: s.EmploymentType || prev.EmploymentType,
        ResumeFile: s.ResumeFile || prev.ResumeFile
      }));
    }
  }, [location?.state]);

  const [skills, setSkills] = useState([emptySkill()]);
  const [education, setEducation] = useState([emptyEducation()]);
  const [certifications, setCertifications] = useState([emptyCert()]);
  const [workExperience, setWorkExperience] = useState([emptyWork()]);
  const [documents, setDocuments] = useState([emptyDocument()]);

  const [activeTab, setActiveTab] = useState('basic');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [resumeDragActive, setResumeDragActive] = useState(false);

  // States list based on selected Country
  const [availableStates, setAvailableStates] = useState([]);

  useEffect(() => {
    if (form.LocationCountry) {
      const states = getStatesForCountry(form.LocationCountry);
      setAvailableStates(states || []);
    } else {
      setAvailableStates([]);
    }
  }, [form.LocationCountry]);

  // Tab definitions with descriptions for executive sidebar layout
  const tabs = [
    { id: 'basic', label: 'Basic Info', desc: 'Identity & preferences', icon: User },
    { id: 'contact', label: 'Contact & Location', desc: 'Email, phone & address', icon: MapPin },
    { id: 'resume', label: 'Resume', desc: 'Upload your CV / profile', icon: Upload },
    { id: 'skills', label: 'Skills', desc: 'Core competencies & tech', icon: Sparkles },
    { id: 'work', label: 'Experience', desc: 'Employment history', icon: Briefcase },
    { id: 'education', label: 'Education', desc: 'Degrees & academics', icon: GraduationCap },
    { id: 'certifications', label: 'Certifications', desc: 'Licenses & credentials', icon: Award },
    { id: 'documents', label: 'Documents', desc: 'Additional attachments', icon: FileText }
  ];

  const currentStepIndex = tabs.findIndex(t => t.id === activeTab);
  const progressPercent = Math.round(((currentStepIndex + 1) / tabs.length) * 100);

  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 'basic':
        return !!(form.FirstName && form.LastName && form.JobTitle);
      case 'contact':
        return !!(form.Email && form.Phone);
      case 'resume':
        return !!form.ResumeFile;
      case 'skills':
        return skills.length > 0 && skills.some(s => (typeof s === 'string' ? s.trim() : s.SkillName?.trim()));
      case 'work':
        return workExperience.some(w => w.Company?.trim() || w.JobTitle?.trim());
      case 'education':
        return education.some(e => e.Institution?.trim() || e.Degree?.trim());
      case 'certifications':
        return certifications.some(c => c.CertificationName?.trim());
      case 'documents':
        return documents.some(d => d.File);
      default:
        return false;
    }
  };

  const resumeInputRef = React.useRef(null);

  // Helper for Resume File validation & setting
  const handleResumeFile = (file) => {
    if (!file) return;
    const ext = (file.name || '').substring((file.name || '').lastIndexOf('.')).toLowerCase();
    const validExts = ['.pdf', '.doc', '.docx', '.rtf', '.txt'];
    const validTypes = [
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain', 'application/rtf'
    ];
    if (!validTypes.includes(file.type) && !validExts.includes(ext)) {
      const errMsg = 'Only PDF or Word documents (.pdf, .doc, .docx, .rtf, .txt) allowed';
      setErrors(prev => ({ ...prev, ResumeFile: errMsg }));
      Swal.fire({
        icon: 'warning',
        title: 'Invalid File Format',
        text: errMsg,
        confirmButtonColor: '#3b5bfd'
      });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      const errMsg = 'Resume file must be under 15MB';
      setErrors(prev => ({ ...prev, ResumeFile: errMsg }));
      Swal.fire({
        icon: 'warning',
        title: 'File Too Large',
        text: errMsg,
        confirmButtonColor: '#3b5bfd'
      });
      return;
    }
    setErrors(prev => ({ ...prev, ResumeFile: null }));
    setForm(prev => ({ ...prev, ResumeFile: file }));
  };

  // Helper for Additional Document validation & upload
  const handleDocumentUpload = (index, file) => {
    if (!file) return;
    const ext = (file.name || '').substring((file.name || '').lastIndexOf('.')).toLowerCase();
    const validExts = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.txt', '.rtf', '.webp'];
    const validTypes = [
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg', 'image/png', 'image/jpg', 'image/webp',
      'text/plain', 'application/rtf'
    ];
    if (!validTypes.includes(file.type) && !validExts.includes(ext)) {
      const errMsg = 'Only PDF, Word, Image, or Text documents allowed';
      setErrors(prev => ({ ...prev, [`Document_${index}`]: errMsg }));
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Document Format',
        text: errMsg,
        confirmButtonColor: '#3b5bfd'
      });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      const errMsg = 'Document file must be under 15MB';
      setErrors(prev => ({ ...prev, [`Document_${index}`]: errMsg }));
      Swal.fire({
        icon: 'warning',
        title: 'File Too Large',
        text: errMsg,
        confirmButtonColor: '#3b5bfd'
      });
      return;
    }
    setErrors(prev => ({ ...prev, [`Document_${index}`]: null }));
    const updated = [...documents];
    updated[index].File = file;
    updated[index].FileName = file.name;
    if (!updated[index].DocumentName || !updated[index].DocumentName.trim()) {
      updated[index].DocumentName = file.name.replace(/\.[^/.]+$/, "");
    }
    setDocuments(updated);
  };

  // Change Handler
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'checkbox') {
      setForm(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'LocationCountry') {
      setForm(prev => ({ ...prev, LocationCountry: value, LocationState: '', LocationCity: '' }));
    } else if (name === 'ResumeFile') {
      const file = files?.[0];
      handleResumeFile(file);
    } else if (name === 'FirstName' || name === 'LastName' || name === 'MiddleName') {
      setForm(prev => ({ ...prev, [name]: formatName(value) }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Validation
  const validateCurrentTab = () => {
    const newErrors = {};

    if (activeTab === 'basic') {
      if (!form.FirstName.trim()) newErrors.FirstName = 'First Name is required';
      if (!form.LastName.trim()) newErrors.LastName = 'Last Name is required';
      if (!form.JobTitle.trim()) newErrors.JobTitle = 'Job Title is required';
    }

    if (activeTab === 'contact') {
      if (!form.Email.trim()) {
        newErrors.Email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(form.Email)) {
        newErrors.Email = 'Invalid email address';
      }
      const rawPhone = form.Mobile || form.Phone;
      if (!rawPhone || !rawPhone.trim()) {
        newErrors.Mobile = 'Phone number is required';
      }
    }

    if (activeTab === 'resume') {
      if (!form.ResumeFile) {
        newErrors.ResumeFile = 'Please upload your resume';
      }
    }

    if (activeTab === 'skills') {
      const hasValidSkill = skills.some(s => s && s.SkillName && s.SkillName.trim().length > 0);
      if (!hasValidSkill) {
        newErrors.Skills = 'Please add at least one skill before proceeding.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateCurrentTab()) {
      return;
    }
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id);
      window.scrollTo({ top: 120, behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
    const currentIndex = tabs.findIndex(t => t.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
      window.scrollTo({ top: 120, behavior: 'smooth' });
    }
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Global verification of critical required fields
    const newErrors = {};
    if (!form.FirstName.trim()) newErrors.FirstName = 'First Name is required';
    if (!form.LastName.trim()) newErrors.LastName = 'Last Name is required';
    if (!form.JobTitle.trim()) newErrors.JobTitle = 'Job Title is required';
    if (!form.Email.trim()) newErrors.Email = 'Email is required';
    if (!form.ResumeFile) newErrors.ResumeFile = 'Resume file is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Switch to the tab that has the first error
      if (newErrors.FirstName || newErrors.LastName || newErrors.JobTitle) {
        setActiveTab('basic');
      } else if (newErrors.Email) {
        setActiveTab('contact');
      } else if (newErrors.ResumeFile) {
        setActiveTab('resume');
      }
      Swal.fire({
        icon: 'warning',
        title: 'Required Information Missing',
        text: 'Please fill in all mandatory fields before submitting.',
        confirmButtonColor: '#3b5bfd'
      });
      return;
    }

    try {
      setSubmitting(true);

      const dataToSend = new FormData();
      dataToSend.append('FirstName', form.FirstName.trim());
      dataToSend.append('LastName', form.LastName.trim());
      dataToSend.append('MiddleName', form.MiddleName.trim());
      dataToSend.append('EmailID', form.Email.trim().toLowerCase());
      dataToSend.append('Phone', form.Phone.trim());
      dataToSend.append('Mobile', (form.Mobile || form.Phone).trim());
      dataToSend.append('JobTitle', form.JobTitle.trim());
      dataToSend.append('YearsOfExperience', form.YearsOfExperience || '');
      dataToSend.append('CandidateStatus', form.CandidateStatus || 'Available');
      dataToSend.append('RemoteStatus', form.RemoteStatus || 'Remote');
      dataToSend.append('WorkAuthorization', form.WorkAuthorization || '');
      dataToSend.append('EmploymentType', form.EmploymentType || '');
      dataToSend.append('SecurityClearance', form.SecurityClearance || '');
      dataToSend.append('WillingToRelocate', form.WillingToRelocate ? 'true' : 'false');
      dataToSend.append('IsBench', 'true'); // ALWAYS bench candidate
      dataToSend.append('Gender', form.Gender || '');
      dataToSend.append('Country', form.LocationCountry || '');
      dataToSend.append('State', form.LocationState || '');
      dataToSend.append('City', form.LocationCity || '');
      
      const locStr = [form.LocationCity, form.LocationState, form.LocationCountry].filter(Boolean).join(', ');
      dataToSend.append('CurrentLocation', locStr || form.CurrentLocation || '');
      dataToSend.append('ProfileSummary', form.ProfileSummary || '');
      dataToSend.append('LinkedInProfile', form.LinkedInUrl || '');
      dataToSend.append('GitHubUrl', form.GitHubUrl || '');
      dataToSend.append('TwitterUrl', form.TwitterUrl || '');
      dataToSend.append('VideoResumeUrl', form.VideoResumeUrl || '');

      // Rate Fields
      dataToSend.append('ExpectedRateFrom', form.ExpectedRateFrom || '');
      dataToSend.append('ExpectedRateTo', form.ExpectedRateTo || '');
      dataToSend.append('ExpectedRateType', form.ExpectedRateType || 'Hourly');
      dataToSend.append('CurrentRate', form.CurrentRate || '');
      dataToSend.append('NoticePeriod', form.NoticePeriod || '');

      // Referrer / Source
      if (referrer) {
        dataToSend.append('ReferredBy', referrer);
        dataToSend.append('Source', `Bench Referral (${referrer})`);
      } else {
        dataToSend.append('Source', 'Join Our Bench Public Page');
      }

      // Skills
      const cleanedSkills = skills
        .filter(s => s && s.SkillName && s.SkillName.trim().length > 0)
        .map(s => ({
          SkillName: s.SkillName.trim(),
          SkillType: s.SkillType || 'Intermediate'
        }));
      dataToSend.append('Skills', JSON.stringify(cleanedSkills));

      // Work Experience
      const cleanedWork = workExperience
        .filter(w => w.Company.trim() || w.JobTitle.trim())
        .map(w => ({
          Company: w.Company.trim(),
          JobTitle: w.JobTitle.trim(),
          StartDate: w.StartDate || '',
          EndDate: w.EndDate || '',
          IsCurrent: !!w.IsCurrent,
          Description: (w.Description || '').trim()
        }));
      dataToSend.append('WorkExperience', JSON.stringify(cleanedWork));

      // Education
      const cleanedEdu = education
        .filter(e => e.Institution.trim() || e.Degree.trim())
        .map(e => ({
          Institution: e.Institution.trim(),
          Degree: e.Degree.trim(),
          FieldOfStudy: (e.FieldOfStudy || '').trim(),
          StartDate: e.StartDate || '',
          EndDate: e.EndDate || '',
          GPA: (e.GPA || '').trim()
        }));
      dataToSend.append('Education', JSON.stringify(cleanedEdu));

      // Certifications
      const cleanedCerts = certifications
        .filter(c => c.CertificationName.trim())
        .map(c => ({
          CertificationName: c.CertificationName.trim(),
          IssuingOrganization: (c.IssuingOrganization || '').trim(),
          IssueDate: c.IssueDate || '',
          ExpiryDate: c.ExpiryDate || '',
          CredentialId: (c.CredentialId || '').trim(),
          CredentialUrl: (c.CredentialUrl || '').trim()
        }));
      dataToSend.append('Certifications', JSON.stringify(cleanedCerts));

      // Primary Resume File
      if (form.ResumeFile) {
        dataToSend.append('resume', form.ResumeFile);
      }

      // Additional Documents
      if (documents.length > 0) {
        const docsMeta = [];
        documents.forEach((doc, idx) => {
          if (doc.File) {
            const fieldKey = `document_file_${idx}`;
            dataToSend.append(fieldKey, doc.File);
            docsMeta.push({
              fieldName: fieldKey,
              documentName: doc.DocumentName || doc.File.name,
              documentType: doc.DocumentType || 'OTHER',
              category: doc.DocumentCategory || 'Other'
            });
          }
        });
        if (docsMeta.length > 0) {
          dataToSend.append('documents_meta', JSON.stringify(docsMeta));
        }
      }

      const response = await fetch(`${BASE_URL}/api/ats/bench-apply`, {
        method: 'POST',
        body: dataToSend
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to submit bench application');
      }

      setSuccessData(result);
    } catch (err) {
      console.error('❌ Bench registration error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Submission Error',
        text: err.message || 'Could not complete bench enrollment. Please try again.',
        confirmButtonColor: '#3b5bfd'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="job-bench-page">
      {/* Brand Header with Prophecy Techs Logo & Navigation */}
      <header className="job-bench-header">
        <div className="job-bench-logo" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <img
            src={prophecyLogo2}
            alt="Prophecy Techs Icon"
            style={{ height: '42px', width: 'auto', objectFit: 'contain', cursor: 'pointer' }}
            onClick={() => navigate('/careers')}
          />
          <img
            src={prophecyLogo}
            alt="Prophecy Techs"
            style={{ height: '28px', width: 'auto', objectFit: 'contain', cursor: 'pointer' }}
            onClick={() => navigate('/careers')}
          />
        </div>
        <div className="job-bench-header-right">
          <button
            type="button"
            className="job-bench-back-to-careers-btn"
            onClick={() => navigate('/careers')}
            title="Return to Careers Portal"
          >
            <ArrowLeft size={16} />
            <span>Back to Careers</span>
          </button>
          <div className="job-bench-badge-tag">
            <Layers size={14} />
            <span>Active Bench Enrollment</span>
          </div>
        </div>
      </header>

      {/* Page Header / Top Title Bar */}
      <section className="job-bench-hero-bar">
        <div className="job-bench-hero-inner">
          <div className="job-bench-hero-top-row">
            <button
              type="button"
              className="job-bench-breadcrumb-back"
              onClick={() => navigate('/careers')}
            >
              <ArrowLeft size={14} />
              <span>Back to Careers</span>
            </button>
            <div className="job-bench-hero-badge">
              <Sparkles size={14} className="job-bench-hero-sparkle" />
              <span>Fast-Tracked Talent Program</span>
            </div>
          </div>
          <h1 className="job-bench-main-title">
            Join Our <span className="job-bench-hero-highlight">Elite Talent Bench</span>
          </h1>
          <p className="job-bench-main-subtitle">
            Get matched with premier Fortune 500 client projects, direct enterprise consulting roles, and exclusive contract placements. Complete the profile below to become an active candidate.
          </p>
        </div>
      </section>

      {/* Main 2-Column Split Workspace */}
      <div className="job-bench-workspace">
        {/* Left Column: Sticky Stepper & Trust Card */}
        <aside className="job-bench-sidebar">
          {/* Progress Card */}
          <div className="job-bench-progress-widget">
            <div className="job-bench-progress-head">
              <span className="job-bench-progress-label">Application Progress</span>
              <span className="job-bench-progress-val">{progressPercent}%</span>
            </div>
            <div className="job-bench-progress-track">
              <div
                className="job-bench-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="job-bench-progress-step-text">
              Step {currentStepIndex + 1} of {tabs.length} • {tabs[currentStepIndex]?.label}
            </div>
          </div>

          {/* Vertical Step Navigation */}
          <nav className="job-bench-nav-card" aria-label="Application Steps">
            <div className="job-bench-nav-list">
              {tabs.map((tab, idx) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const isCompleted = isStepComplete(tab.id);
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`job-bench-step-btn ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                    onClick={() => {
                      if (idx > currentStepIndex) {
                        if (!validateCurrentTab()) return;
                      }
                      setActiveTab(tab.id);
                    }}
                  >
                    <div className="job-bench-step-indicator">
                      {isCompleted && !isActive ? (
                        <Check size={14} className="job-bench-check-icon" />
                      ) : (
                        <span>{idx + 1}</span>
                      )}
                    </div>
                    <div className="job-bench-step-info">
                      <div className="job-bench-step-title-row">
                        <span className="job-bench-step-title">{tab.label}</span>
                        {isActive && <span className="job-bench-active-pill">In Progress</span>}
                      </div>
                      <span className="job-bench-step-desc">{tab.desc}</span>
                    </div>
                    <Icon size={16} className="job-bench-step-trailing-icon" />
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Value / Trust Card */}
          <div className="job-bench-perks-card">
            <div className="job-bench-perks-title">
              <ShieldCheck size={16} color="#3b5bfd" />
              <span>Why Prophecy Bench?</span>
            </div>
            <ul className="job-bench-perks-list">
              <li>
                <CheckCircle2 size={13} className="job-bench-perk-check" />
                <span>Direct client contract opportunities</span>
              </li>
              <li>
                <CheckCircle2 size={13} className="job-bench-perk-check" />
                <span>Expedited technical screening</span>
              </li>
              <li>
                <CheckCircle2 size={13} className="job-bench-perk-check" />
                <span>Dedicated bench sales & legal team</span>
              </li>
            </ul>
          </div>
        </aside>

        {/* Right Column: Active Step Card & Form */}
        <main className="job-bench-content">
          <div className="job-bench-card">
            {/* Form Body */}
            <form onSubmit={handleSubmit}>
              <div className="job-bench-body">
              {/* TAB 1: BASIC INFO */}
              {activeTab === 'basic' && (
                <div>
                  <div className="job-bench-section-title">
                    <User size={20} color="#3b5bfd" />
                    <span>Basic Information</span>
                  </div>
                  <p className="job-bench-section-desc">
                    Tell us your core professional identity and preferences.
                  </p>

                  <div className="job-bench-grid-3">
                    <div className="job-bench-field">
                      <label className="job-bench-label">
                        First Name <span className="job-bench-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="FirstName"
                        className={`job-bench-input ${errors.FirstName ? 'has-error' : ''}`}
                        placeholder="Enter first name"
                        value={form.FirstName}
                        onChange={handleChange}
                      />
                      {errors.FirstName && <span className="job-bench-error-msg">{errors.FirstName}</span>}
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">Middle Name</label>
                      <input
                        type="text"
                        name="MiddleName"
                        className="job-bench-input"
                        placeholder="Enter middle name"
                        value={form.MiddleName}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">
                        Last Name <span className="job-bench-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="LastName"
                        className={`job-bench-input ${errors.LastName ? 'has-error' : ''}`}
                        placeholder="Enter last name"
                        value={form.LastName}
                        onChange={handleChange}
                      />
                      {errors.LastName && <span className="job-bench-error-msg">{errors.LastName}</span>}
                    </div>
                  </div>

                  <div className="job-bench-grid-3" style={{ marginTop: '16px' }}>
                    <div className="job-bench-field">
                      <label className="job-bench-label">
                        Job Title <span className="job-bench-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="JobTitle"
                        className={`job-bench-input ${errors.JobTitle ? 'has-error' : ''}`}
                        placeholder="e.g. Senior Software Engineer"
                        value={form.JobTitle}
                        onChange={handleChange}
                      />
                      {errors.JobTitle && <span className="job-bench-error-msg">{errors.JobTitle}</span>}
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">Years of Experience</label>
                      <input
                        type="number"
                        step="0.5"
                        name="YearsOfExperience"
                        className="job-bench-input"
                        placeholder="e.g. 5"
                        value={form.YearsOfExperience}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">Candidate Status</label>
                      <select
                        name="CandidateStatus"
                        className="job-bench-select"
                        value={form.CandidateStatus}
                        onChange={handleChange}
                      >
                        {renderOptions(STATUS_OPTIONS_FORM)}
                      </select>
                    </div>
                  </div>

                  <div className="job-bench-grid-3" style={{ marginTop: '16px' }}>
                    <div className="job-bench-field">
                      <label className="job-bench-label">Remote / Work Type</label>
                      <select
                        name="RemoteStatus"
                        className="job-bench-select"
                        value={form.RemoteStatus}
                        onChange={handleChange}
                      >
                        {renderOptions(REMOTE_OPTIONS)}
                      </select>
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">Work Authorization</label>
                      <select
                        name="WorkAuthorization"
                        className="job-bench-select"
                        value={form.WorkAuthorization}
                        onChange={handleChange}
                      >
                        {renderOptions(WORK_AUTHORIZATION_OPTIONS, 'Select Work Auth')}
                      </select>
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">Employment Type</label>
                      <select
                        name="EmploymentType"
                        className="job-bench-select"
                        value={form.EmploymentType}
                        onChange={handleChange}
                      >
                        {renderOptions(EMPLOYMENT_TYPE_OPTIONS, 'Select Employment Type')}
                      </select>
                    </div>
                  </div>

                  <div className="job-bench-grid-3" style={{ marginTop: '16px' }}>
                    <div className="job-bench-field">
                      <label className="job-bench-label">Security Clearance</label>
                      <select
                        name="SecurityClearance"
                        className="job-bench-select"
                        value={form.SecurityClearance}
                        onChange={handleChange}
                      >
                        {renderOptions(SECURITY_CLEARANCE_OPTIONS, 'Select Clearance')}
                      </select>
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">Gender</label>
                      <select
                        name="Gender"
                        className="job-bench-select"
                        value={form.Gender}
                        onChange={handleChange}
                      >
                        {renderOptions(GENDER_OPTIONS, 'Select Gender')}
                      </select>
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">Marital Status</label>
                      <select
                        name="MaritalStatus"
                        className="job-bench-select"
                        value={form.MaritalStatus}
                        onChange={handleChange}
                      >
                        {renderOptions(MARITAL_STATUS_OPTIONS, 'Select Marital Status')}
                      </select>
                    </div>
                  </div>

                  <div className="job-bench-grid-3" style={{ marginTop: '16px' }}>
                    <div className="job-bench-field">
                      <label className="job-bench-label">Industry</label>
                      <select
                        name="Industry"
                        className="job-bench-select"
                        value={form.Industry}
                        onChange={handleChange}
                      >
                        {renderOptions(INDUSTRY_OPTIONS, 'Select Industry')}
                      </select>
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label" style={{ visibility: 'hidden' }}>Relocate</label>
                      <label className="job-bench-checkbox-wrap" style={{ marginTop: '4px' }}>
                        <input
                          type="checkbox"
                          name="WillingToRelocate"
                          className="job-bench-checkbox"
                          checked={form.WillingToRelocate}
                          onChange={handleChange}
                        />
                        <span className="job-bench-label">Willing to Relocate</span>
                      </label>
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label" style={{ visibility: 'hidden' }}>Bench</label>
                      <label className="job-bench-checkbox-wrap" style={{ marginTop: '4px' }}>
                        <input
                          type="checkbox"
                          name="IsBench"
                          className="job-bench-checkbox"
                          checked={form.IsBench}
                          disabled
                        />
                        <span className="job-bench-label">Bench Candidate (Enrolled)</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: CONTACT & LOCATION */}
              {activeTab === 'contact' && (
                <div>
                  <div className="job-bench-section-title">
                    <MapPin size={20} color="#3b5bfd" />
                    <span>Contact Information & Location</span>
                  </div>
                  <p className="job-bench-section-desc">
                    How can our recruitment team reach you for project interviews?
                  </p>

                  <div className="job-bench-grid-3">
                    <div className="job-bench-field">
                      <label className="job-bench-label">
                        Email Address <span className="job-bench-required">*</span>
                      </label>
                      <input
                        type="email"
                        name="Email"
                        className={`job-bench-input ${errors.Email ? 'has-error' : ''}`}
                        placeholder="john.doe@example.com"
                        value={form.Email}
                        onChange={handleChange}
                      />
                      {errors.Email && <span className="job-bench-error-msg">{errors.Email}</span>}
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">
                        Mobile Number <span className="job-bench-required">*</span>
                      </label>
                      <PhoneInput
                        country={'us'}
                        value={form.Mobile || form.Phone}
                        onChange={(phone) => {
                          setForm(p => ({ ...p, Mobile: phone, Phone: phone }));
                          if (errors.Mobile) setErrors(p => ({ ...p, Mobile: null }));
                        }}
                        inputStyle={{
                          width: '100%',
                          height: '42px',
                          fontSize: '14px',
                          borderRadius: '8px',
                          borderColor: errors.Mobile ? '#ef4444' : '#e2e8f0'
                        }}
                      />
                      {errors.Mobile && <span className="job-bench-error-msg">{errors.Mobile}</span>}
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">Notice Period / Availability</label>
                      <input
                        type="text"
                        name="NoticePeriod"
                        className="job-bench-input"
                        placeholder="e.g. Immediate / 2 Weeks"
                        value={form.NoticePeriod}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="job-bench-grid-3" style={{ marginTop: '16px' }}>
                    <div className="job-bench-field">
                      <label className="job-bench-label">Country</label>
                      <select
                        name="LocationCountry"
                        className="job-bench-select"
                        value={form.LocationCountry}
                        onChange={handleChange}
                      >
                        {renderOptions(countriesData, 'Select Country')}
                      </select>
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">State</label>
                      {availableStates && availableStates.length > 0 ? (
                        <select
                          name="LocationState"
                          className="job-bench-select"
                          value={form.LocationState}
                          onChange={handleChange}
                        >
                          {renderOptions(availableStates, 'Select State')}
                        </select>
                      ) : (
                        <input
                          type="text"
                          name="LocationState"
                          className="job-bench-input"
                          placeholder="Enter state"
                          value={form.LocationState}
                          onChange={handleChange}
                        />
                      )}
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">City</label>
                      <input
                        type="text"
                        name="LocationCity"
                        className="job-bench-input"
                        placeholder="Enter city"
                        value={form.LocationCity}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="job-bench-grid-3" style={{ marginTop: '16px' }}>
                    <div className="job-bench-field">
                      <label className="job-bench-label">Expected Min Rate</label>
                      <input
                        type="text"
                        name="ExpectedRateFrom"
                        className="job-bench-input"
                        placeholder="e.g. 60"
                        value={form.ExpectedRateFrom}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">Expected Max Rate</label>
                      <input
                        type="text"
                        name="ExpectedRateTo"
                        className="job-bench-input"
                        placeholder="e.g. 80"
                        value={form.ExpectedRateTo}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">Rate Type</label>
                      <select
                        name="ExpectedRateType"
                        className="job-bench-select"
                        value={form.ExpectedRateType}
                        onChange={handleChange}
                      >
                        {renderOptions(RATE_TYPE_OPTIONS)}
                      </select>
                    </div>
                  </div>

                  <div className="job-bench-grid-3" style={{ marginTop: '16px' }}>
                    <div className="job-bench-field">
                      <label className="job-bench-label">LinkedIn Profile URL</label>
                      <input
                        type="url"
                        name="LinkedInUrl"
                        className="job-bench-input"
                        placeholder="https://linkedin.com/in/username"
                        value={form.LinkedInUrl}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">GitHub URL</label>
                      <input
                        type="url"
                        name="GitHubUrl"
                        className="job-bench-input"
                        placeholder="https://github.com/username"
                        value={form.GitHubUrl}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="job-bench-field">
                      <label className="job-bench-label">Portfolio / Website URL</label>
                      <input
                        type="url"
                        name="TwitterUrl"
                        className="job-bench-input"
                        placeholder="https://portfolio.me"
                        value={form.TwitterUrl}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="job-bench-field" style={{ marginTop: '16px' }}>
                    <label className="job-bench-label">Professional Summary / Bio</label>
                    <textarea
                      name="ProfileSummary"
                      rows="3"
                      className="job-bench-textarea"
                      placeholder="Brief overview of your expertise, key accomplishments, and project goals..."
                      value={form.ProfileSummary}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              )}

              {/* TAB 3: RESUME UPLOAD */}
              {activeTab === 'resume' && (
                <div>
                  <div className="job-bench-section-title">
                    <Upload size={20} color="#3b5bfd" />
                    <span>Upload Resume</span>
                  </div>
                  <p className="job-bench-section-desc">
                    Attach your latest CV / Resume (.pdf, .doc, .docx).
                  </p>

                  <div
                    className={`job-bench-dropzone ${errors.ResumeFile ? 'has-error' : ''} ${resumeDragActive ? 'drag-active' : ''}`}
                    onClick={() => {
                      if (resumeInputRef.current) {
                        resumeInputRef.current.click();
                      }
                    }}
                    onDragOver={(e) => { e.preventDefault(); setResumeDragActive(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setResumeDragActive(false); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setResumeDragActive(false);
                      if (e.dataTransfer.files?.[0]) {
                        handleResumeFile(e.dataTransfer.files[0]);
                      }
                    }}
                  >
                    <input
                      ref={resumeInputRef}
                      id="bench-resume-input"
                      type="file"
                      name="ResumeFile"
                      accept=".pdf,.doc,.docx,.rtf,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleResumeFile(file);
                        e.target.value = '';
                      }}
                    />
                    <div className="job-bench-dropzone-icon">
                      <Upload size={26} />
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                      Drag & Drop your resume here, or
                    </div>
                    <div style={{ marginTop: '12px' }}>
                      <button
                        type="button"
                        className="job-bench-file-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (resumeInputRef.current) {
                            resumeInputRef.current.click();
                          }
                        }}
                      >
                        <Upload size={15} style={{ pointerEvents: 'none' }} />
                        <span style={{ pointerEvents: 'none' }}>
                          {form.ResumeFile ? 'Change Resume File' : 'Choose Resume File'}
                        </span>
                      </button>
                    </div>
                    <p style={{ fontSize: '12.5px', color: '#64748b', margin: '10px 0 0' }}>
                      Supported formats: PDF, DOC, DOCX, RTF, TXT (Up to 15MB)
                    </p>

                    {form.ResumeFile && (
                      <div className="job-bench-file-pill" onClick={(e) => e.stopPropagation()}>
                        <CheckCircle2 size={18} color="#3b5bfd" />
                        <span style={{ fontSize: '13.5px', fontWeight: '600', color: '#0f172a' }}>
                          {form.ResumeFile.name}
                        </span>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                          ({(form.ResumeFile.size / 1024).toFixed(0)} KB)
                        </span>
                        <button
                          type="button"
                          className="job-bench-delete-btn"
                          title="Remove file"
                          onClick={(e) => {
                            e.stopPropagation();
                            setForm(p => ({ ...p, ResumeFile: null }));
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </div>
                  {errors.ResumeFile && <span className="job-bench-error-msg">{errors.ResumeFile}</span>}
                </div>
              )}

              {/* TAB 4: SKILLS */}
              {activeTab === 'skills' && (
                <div>
                  <div className="job-bench-section-title">
                    <Sparkles size={20} color="#3b5bfd" />
                    <span>Skills & Technologies</span>
                  </div>
                  <p className="job-bench-section-desc">
                    Add the primary technical and domain skills in your arsenal.
                  </p>
                  {errors.Skills && (
                    <p className="job-bench-error-msg" style={{ margin: '8px 0 16px 0', color: '#ef4444', fontWeight: 500 }}>
                      {errors.Skills}
                    </p>
                  )}

                  {skills.map((skill, index) => (
                    <div key={index} className="job-bench-repeat-card">
                      <div className="job-bench-repeat-header">
                        <span className="job-bench-repeat-title">Skill #{index + 1}</span>
                        {skills.length > 1 && (
                          <button
                            type="button"
                            className="job-bench-delete-btn"
                            onClick={() => setSkills(skills.filter((_, i) => i !== index))}
                          >
                            <Trash2 size={14} /> Remove
                          </button>
                        )}
                      </div>
                      <div className="job-bench-grid-2">
                        <div className="job-bench-field">
                          <label className="job-bench-label">Skill Name *</label>
                          <input
                            type="text"
                            className={`job-bench-input ${errors.Skills && !skill.SkillName.trim() ? 'has-error' : ''}`}
                            placeholder="e.g. React.js, Python, AWS, Snowflake"
                            value={skill.SkillName}
                            onChange={(e) => {
                              const updated = [...skills];
                              updated[index].SkillName = e.target.value;
                              setSkills(updated);
                              if (errors.Skills) {
                                setErrors(prev => {
                                  const copy = { ...prev };
                                  delete copy.Skills;
                                  return copy;
                                });
                              }
                            }}
                          />
                        </div>
                        <div className="job-bench-field">
                          <label className="job-bench-label">Skill Level</label>
                          <select
                            className="job-bench-select"
                            value={skill.SkillType || 'Intermediate'}
                            onChange={(e) => {
                              const updated = [...skills];
                              updated[index].SkillType = e.target.value;
                              setSkills(updated);
                            }}
                          >
                            {renderOptions(SKILL_LEVEL_OPTIONS)}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="job-bench-add-btn"
                    onClick={() => setSkills([...skills, emptySkill()])}
                  >
                    <Plus size={16} /> Add Another Skill
                  </button>
                </div>
              )}

              {/* TAB 5: WORK EXPERIENCE */}
              {activeTab === 'work' && (
                <div>
                  <div className="job-bench-section-title">
                    <Briefcase size={20} color="#3b5bfd" />
                    <span>Work Experience</span>
                  </div>
                  <p className="job-bench-section-desc">
                    Detail your previous employment and client projects.
                  </p>

                  {workExperience.map((work, index) => (
                    <div key={index} className="job-bench-repeat-card">
                      <div className="job-bench-repeat-header">
                        <span className="job-bench-repeat-title">Experience #{index + 1}</span>
                        {workExperience.length > 1 && (
                          <button
                            type="button"
                            className="job-bench-delete-btn"
                            onClick={() => setWorkExperience(workExperience.filter((_, i) => i !== index))}
                          >
                            <Trash2 size={14} /> Remove
                          </button>
                        )}
                      </div>

                      <div className="job-bench-grid-2">
                        <div className="job-bench-field">
                          <label className="job-bench-label">Company / Client</label>
                          <input
                            type="text"
                            className="job-bench-input"
                            placeholder="e.g. Google, Deloitte, Infosys"
                            value={work.Company}
                            onChange={(e) => {
                              const updated = [...workExperience];
                              updated[index].Company = e.target.value;
                              setWorkExperience(updated);
                            }}
                          />
                        </div>

                        <div className="job-bench-field">
                          <label className="job-bench-label">Job Title</label>
                          <input
                            type="text"
                            className="job-bench-input"
                            placeholder="e.g. Senior Cloud Architect"
                            value={work.JobTitle}
                            onChange={(e) => {
                              const updated = [...workExperience];
                              updated[index].JobTitle = e.target.value;
                              setWorkExperience(updated);
                            }}
                          />
                        </div>
                      </div>

                      <div className="job-bench-grid-3" style={{ marginTop: '12px' }}>
                        <div className="job-bench-field">
                          <label className="job-bench-label">Start Date</label>
                          <input
                            type="date"
                            className="job-bench-input"
                            value={work.StartDate}
                            onChange={(e) => {
                              const updated = [...workExperience];
                              updated[index].StartDate = e.target.value;
                              setWorkExperience(updated);
                            }}
                          />
                        </div>

                        <div className="job-bench-field">
                          <label className="job-bench-label">End Date</label>
                          <input
                            type="date"
                            className="job-bench-input"
                            disabled={work.IsCurrent}
                            value={work.EndDate}
                            onChange={(e) => {
                              const updated = [...workExperience];
                              updated[index].EndDate = e.target.value;
                              setWorkExperience(updated);
                            }}
                          />
                        </div>

                        <label className="job-bench-checkbox-wrap" style={{ marginTop: '24px' }}>
                          <input
                            type="checkbox"
                            className="job-bench-checkbox"
                            checked={work.IsCurrent}
                            onChange={(e) => {
                              const updated = [...workExperience];
                              updated[index].IsCurrent = e.target.checked;
                              if (e.target.checked) updated[index].EndDate = '';
                              setWorkExperience(updated);
                            }}
                          />
                          <span className="job-bench-label">Currently Working</span>
                        </label>
                      </div>

                      <div className="job-bench-field" style={{ marginTop: '12px' }}>
                        <label className="job-bench-label">Role Description / Achievements</label>
                        <textarea
                          rows="3"
                          className="job-bench-textarea"
                          placeholder="Key duties, technologies utilized, project highlights..."
                          value={work.Description}
                          onChange={(e) => {
                            const updated = [...workExperience];
                            updated[index].Description = e.target.value;
                            setWorkExperience(updated);
                          }}
                        />
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="job-bench-add-btn"
                    onClick={() => setWorkExperience([...workExperience, emptyWork()])}
                  >
                    <Plus size={16} /> Add Another Experience
                  </button>
                </div>
              )}

              {/* TAB 6: EDUCATION */}
              {activeTab === 'education' && (
                <div>
                  <div className="job-bench-section-title">
                    <GraduationCap size={20} color="#3b5bfd" />
                    <span>Education</span>
                  </div>
                  <p className="job-bench-section-desc">
                    Your university degrees and academic achievements.
                  </p>

                  {education.map((edu, index) => (
                    <div key={index} className="job-bench-repeat-card">
                      <div className="job-bench-repeat-header">
                        <span className="job-bench-repeat-title">Education #{index + 1}</span>
                        {education.length > 1 && (
                          <button
                            type="button"
                            className="job-bench-delete-btn"
                            onClick={() => setEducation(education.filter((_, i) => i !== index))}
                          >
                            <Trash2 size={14} /> Remove
                          </button>
                        )}
                      </div>

                      <div className="job-bench-grid-2">
                        <div className="job-bench-field">
                          <label className="job-bench-label">Institution / University</label>
                          <input
                            type="text"
                            className="job-bench-input"
                            placeholder="e.g. University of Texas at Dallas"
                            value={edu.Institution}
                            onChange={(e) => {
                              const updated = [...education];
                              updated[index].Institution = e.target.value;
                              setEducation(updated);
                            }}
                          />
                        </div>

                        <div className="job-bench-field">
                          <label className="job-bench-label">Degree</label>
                          <input
                            type="text"
                            className="job-bench-input"
                            placeholder="e.g. Bachelor of Science / Master's"
                            value={edu.Degree}
                            onChange={(e) => {
                              const updated = [...education];
                              updated[index].Degree = e.target.value;
                              setEducation(updated);
                            }}
                          />
                        </div>
                      </div>

                      <div className="job-bench-grid-3" style={{ marginTop: '12px' }}>
                        <div className="job-bench-field">
                          <label className="job-bench-label">Field of Study</label>
                          <input
                            type="text"
                            className="job-bench-input"
                            placeholder="e.g. Computer Science"
                            value={edu.FieldOfStudy}
                            onChange={(e) => {
                              const updated = [...education];
                              updated[index].FieldOfStudy = e.target.value;
                              setEducation(updated);
                            }}
                          />
                        </div>

                        <div className="job-bench-field">
                          <label className="job-bench-label">Start Date</label>
                          <input
                            type="date"
                            className="job-bench-input"
                            value={edu.StartDate}
                            onChange={(e) => {
                              const updated = [...education];
                              updated[index].StartDate = e.target.value;
                              setEducation(updated);
                            }}
                          />
                        </div>

                        <div className="job-bench-field">
                          <label className="job-bench-label">End Date / Graduation</label>
                          <input
                            type="date"
                            className="job-bench-input"
                            value={edu.EndDate}
                            onChange={(e) => {
                              const updated = [...education];
                              updated[index].EndDate = e.target.value;
                              setEducation(updated);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="job-bench-add-btn"
                    onClick={() => setEducation([...education, emptyEducation()])}
                  >
                    <Plus size={16} /> Add Another Education
                  </button>
                </div>
              )}

              {/* TAB 7: CERTIFICATIONS */}
              {activeTab === 'certifications' && (
                <div>
                  <div className="job-bench-section-title">
                    <Award size={20} color="#3b5bfd" />
                    <span>Certifications</span>
                  </div>
                  <p className="job-bench-section-desc">
                    Add any active professional accreditations or credentials.
                  </p>

                  {certifications.map((cert, index) => (
                    <div key={index} className="job-bench-repeat-card">
                      <div className="job-bench-repeat-header">
                        <span className="job-bench-repeat-title">Certification #{index + 1}</span>
                        {certifications.length > 1 && (
                          <button
                            type="button"
                            className="job-bench-delete-btn"
                            onClick={() => setCertifications(certifications.filter((_, i) => i !== index))}
                          >
                            <Trash2 size={14} /> Remove
                          </button>
                        )}
                      </div>

                      <div className="job-bench-grid-2">
                        <div className="job-bench-field">
                          <label className="job-bench-label">Certification Name</label>
                          <input
                            type="text"
                            className="job-bench-input"
                            placeholder="e.g. AWS Certified Solutions Architect"
                            value={cert.CertificationName}
                            onChange={(e) => {
                              const updated = [...certifications];
                              updated[index].CertificationName = e.target.value;
                              setCertifications(updated);
                            }}
                          />
                        </div>

                        <div className="job-bench-field">
                          <label className="job-bench-label">Issuing Organization</label>
                          <input
                            type="text"
                            className="job-bench-input"
                            placeholder="e.g. Amazon Web Services"
                            value={cert.IssuingOrganization}
                            onChange={(e) => {
                              const updated = [...certifications];
                              updated[index].IssuingOrganization = e.target.value;
                              setCertifications(updated);
                            }}
                          />
                        </div>
                      </div>

                      <div className="job-bench-grid-2" style={{ marginTop: '12px' }}>
                        <div className="job-bench-field">
                          <label className="job-bench-label">Credential ID</label>
                          <input
                            type="text"
                            className="job-bench-input"
                            placeholder="e.g. AWS-ASA-12345"
                            value={cert.CredentialId}
                            onChange={(e) => {
                              const updated = [...certifications];
                              updated[index].CredentialId = e.target.value;
                              setCertifications(updated);
                            }}
                          />
                        </div>

                        <div className="job-bench-field">
                          <label className="job-bench-label">Credential URL</label>
                          <input
                            type="url"
                            className="job-bench-input"
                            placeholder="https://verify.cert.com/..."
                            value={cert.CredentialUrl}
                            onChange={(e) => {
                              const updated = [...certifications];
                              updated[index].CredentialUrl = e.target.value;
                              setCertifications(updated);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="job-bench-add-btn"
                    onClick={() => setCertifications([...certifications, emptyCert()])}
                  >
                    <Plus size={16} /> Add Another Certification
                  </button>
                </div>
              )}

              {/* TAB 8: DOCUMENTS */}
              {activeTab === 'documents' && (
                <div>
                  <div className="job-bench-section-title">
                    <FileText size={20} color="#3b5bfd" />
                    <span>Additional Documents (Optional)</span>
                  </div>
                  <p className="job-bench-section-desc">
                    Attach optional supporting files such as photo ID, work authorization copy, certifications, or transcripts.
                  </p>

                  {documents.map((doc, index) => (
                    <div
                      key={index}
                      className="job-bench-repeat-card"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (e.dataTransfer.files?.[0]) {
                          handleDocumentUpload(index, e.dataTransfer.files[0]);
                        }
                      }}
                    >
                      <div className="job-bench-repeat-header">
                        <span className="job-bench-repeat-title">Document #{index + 1}</span>
                        {documents.length > 1 && (
                          <button
                            type="button"
                            className="job-bench-delete-btn"
                            onClick={() => setDocuments(documents.filter((_, i) => i !== index))}
                          >
                            <Trash2 size={14} /> Remove Document
                          </button>
                        )}
                      </div>

                      <div className="job-bench-grid-3">
                        <div className="job-bench-field">
                          <label className="job-bench-label">Document Name</label>
                          <input
                            type="text"
                            className="job-bench-input"
                            placeholder="e.g. Work Authorization / Photo ID"
                            value={doc.DocumentName}
                            onChange={(e) => {
                              const updated = [...documents];
                              updated[index].DocumentName = e.target.value;
                              setDocuments(updated);
                            }}
                          />
                        </div>

                        <div className="job-bench-field">
                          <label className="job-bench-label">Document Type</label>
                          <select
                            className="job-bench-select"
                            value={doc.DocumentType || 'Certificate'}
                            onChange={(e) => {
                              const updated = [...documents];
                              updated[index].DocumentType = e.target.value;
                              setDocuments(updated);
                            }}
                          >
                            <option value="Certificate">Certificate</option>
                            <option value="ID Proof">ID Proof</option>
                            <option value="Work Authorization">Work Authorization</option>
                            <option value="Degree">Degree / Transcript</option>
                            <option value="Offer Letter">Offer Letter</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>

                        <div className="job-bench-field">
                          <label className="job-bench-label">Attach File</label>
                          <div className="job-bench-file-container">
                            <input
                              id={`bench-doc-input-${index}`}
                              type="file"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.rtf,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleDocumentUpload(index, file);
                                }
                                e.target.value = '';
                              }}
                            />
                            <button
                              type="button"
                              className="job-bench-file-btn"
                              onClick={() => {
                                const inputEl = document.getElementById(`bench-doc-input-${index}`);
                                if (inputEl) inputEl.click();
                              }}
                            >
                              <Upload size={14} style={{ pointerEvents: 'none' }} />
                              <span style={{ pointerEvents: 'none' }}>
                                {doc.File ? 'Change File' : 'Choose File'}
                              </span>
                            </button>
                            {doc.File ? (
                              <div className="job-bench-file-info">
                                <CheckCircle2 size={16} color="#3b5bfd" />
                                <span className="job-bench-file-name" title={doc.File.name}>{doc.File.name}</span>
                                <span className="job-bench-file-size">({(doc.File.size / 1024).toFixed(0)} KB)</span>
                                <button
                                  type="button"
                                  className="job-bench-delete-btn"
                                  title="Remove attached file"
                                  style={{ padding: '2px 6px', marginLeft: '4px' }}
                                  onClick={() => {
                                    const updated = [...documents];
                                    updated[index].File = null;
                                    updated[index].FileName = '';
                                    setDocuments(updated);
                                  }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            ) : (
                              <span style={{ fontSize: '12px', color: '#64748b' }}>No file chosen yet</span>
                            )}
                          </div>
                          {errors[`Document_${index}`] && (
                            <span className="job-bench-error-msg">{errors[`Document_${index}`]}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="job-bench-add-btn"
                    onClick={() => setDocuments([...documents, emptyDocument()])}
                  >
                    <Plus size={16} /> Add Another Document
                  </button>
                </div>
              )}
            </div>

            {/* Footer Navigation & Submit */}
            <div className="job-bench-footer">
              <div>
                {currentStepIndex > 0 ? (
                  <button
                    type="button"
                    className="job-bench-btn-prev"
                    onClick={handlePrev}
                  >
                    <ArrowLeft size={16} />
                    <span>Previous Step</span>
                  </button>
                ) : (
                  <div />
                )}
              </div>

              <div className="job-bench-footer-right">
                <span className="job-bench-footer-step-hint">
                  Step {currentStepIndex + 1} of {tabs.length}
                </span>

                {currentStepIndex < tabs.length - 1 ? (
                  <button
                    type="button"
                    className="job-bench-btn-next"
                    onClick={handleNext}
                  >
                    <span>Continue to {tabs[currentStepIndex + 1]?.label}</span>
                    <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="job-bench-btn-submit"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <span>Enrolling into Bench...</span>
                    ) : (
                      <>
                        <ShieldCheck size={18} />
                        <span>Submit Bench Application</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>

      {/* Success Modal / Card Overlay */}
      {successData && (
        <div className="job-bench-success-overlay">
          <div className="job-bench-success-card">
            <div className="job-bench-success-icon">
              <CheckCircle2 size={40} />
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '0 0 10px' }}>
              Welcome to Our Bench!
            </h2>
            <p style={{ fontSize: '14.5px', color: '#475569', lineHeight: '1.6', margin: '0 0 16px' }}>
              Your application has been received successfully. Our bench sales recruiters have added your profile to our active candidate pool and will contact you for matching client roles.
            </p>

            <div className="job-bench-code-box">
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>
                Your Candidate Tracking Code
              </div>
              <div className="job-bench-code-val">
                {successData.candidateCode || 'CAN-CONFIRMED'}
              </div>
            </div>

            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '24px' }}>
              A confirmation email has been dispatched to <strong>{successData.email || form.Email}</strong> with your credentials.
            </p>

            <button
              type="button"
              className="job-bench-btn-submit"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => {
                window.location.href = '/login';
              }}
            >
              <span>Track Application</span>
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
